import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePortvilleBilling } from '../../../../hooks/admin/usePortvilleBilling';
import { PortvilleBill } from '../../../../types/portvilleBilling';
import BillsTable from '../../../../components/admin/portville/bills/BillsTable';
import BillDetailModal from '../../../../components/admin/portville/bills/BillDetailModal';
import BillGenerationModal from '../../../../components/admin/portville/bills/BillGenerationModal';
import PenaltyInputModal from '../../../../components/admin/portville/bills/PenaltyInputModal';
import { generateBillPDF, generateMultipleBillsPDF } from '../../../../utils/pdfUtils';

const BillsManagePage: React.FC = () => {
  // 상태 관리
  const [selectedPeriod, setSelectedPeriod] = useState<{year: number, month: number}>(() => {
    const now = new Date();
    const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const lastYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    
    return {
      year: lastYear,
      month: lastMonth
    };
  });

  const {
    bills,
    loading,
    error,
    refreshData,
    generateBills,
    updateBillStatus,
    updateBillPenalty,
    deleteBill
  } = usePortvilleBilling(selectedPeriod.year, selectedPeriod.month);
  
  
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBill, setSelectedBill] = useState<PortvilleBill | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [penaltyBill, setPenaltyBill] = useState<PortvilleBill | null>(null);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    refreshData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // selectedPeriod 변경 시에만 데이터 새로고침 (usePortvilleBilling 훅에서 자동으로 처리됨)
  // 별도의 refreshData 호출이 불필요함

  // 검색어 정규화 (성능 최적화)
  const normalizedSearchTerm = useMemo(() =>
    searchTerm.toLowerCase().trim(), [searchTerm]
  );

  // 필터링된 청구서 목록 (최적화)
  const filteredBills = useMemo(() => {
    if (bills.length === 0) return [];

    return bills.filter(bill => {
      // 상태 필터 (빠른 체크)
      if (statusFilter !== 'all' && bill.status !== statusFilter) {
        return false;
      }

      // 검색 필터 (검색어가 없으면 빠른 리턴)
      if (!normalizedSearchTerm) {
        return true;
      }

      // 검색 필터 최적화: 가장 일반적인 검색부터
      return bill.roomNumber.toLowerCase().includes(normalizedSearchTerm) ||
        bill.residentInfo?.name.toLowerCase().includes(normalizedSearchTerm) ||
        bill.residentInfo?.phoneNumber.includes(searchTerm) ||
        bill.residentInfo?.organization.toLowerCase().includes(normalizedSearchTerm) ||
        bill.facilityInfo?.facilityName.toLowerCase().includes(normalizedSearchTerm) ||
        bill.facilityInfo?.responsiblePerson.name.toLowerCase().includes(normalizedSearchTerm);
    });
  }, [bills, statusFilter, normalizedSearchTerm, searchTerm]);

  // 통계 계산 최적화 (한 번의 순회로 모든 통계 계산)
  const statistics = useMemo(() => {
    if (filteredBills.length === 0) {
      return {
        total: 0,
        pending: 0,
        paid: 0,
        overdue: 0,
        totalAmount: 0,
        paidAmount: 0,
        paymentRate: 0
      };
    }

    let pending = 0;
    let paid = 0;
    let overdue = 0;
    let totalAmount = 0;
    let paidAmount = 0;

    for (const bill of filteredBills) {
      totalAmount += bill.totalAmount;

      switch (bill.status) {
        case 'pending':
          pending++;
          break;
        case 'paid':
          paid++;
          paidAmount += bill.totalAmount;
          break;
        case 'overdue':
          overdue++;
          break;
      }
    }

    const total = filteredBills.length;

    return {
      total,
      pending,
      paid,
      overdue,
      totalAmount,
      paidAmount,
      paymentRate: total > 0 ? (paid / total) * 100 : 0
    };
  }, [filteredBills]);

  // 청구서 상세 보기
  const handleBillDetail = (bill: PortvilleBill) => {
    setSelectedBill(bill);
    setShowDetailModal(true);
  };

  // 청구서 생성 모달 열기
  const handleGenerateBills = () => {
    setShowGenerationModal(true);
  };

  // 청구서 업데이트 (useCallback으로 최적화)
  const handleUpdateBill = useCallback(async (billId: string, updates: Partial<PortvilleBill>) => {
    try {
      if (updates.status) {
        await updateBillStatus(billId, updates.status);
        await refreshData();
      } else {
        console.warn('현재는 청구서 상태 변경만 지원됩니다.');
      }
    } catch (error) {
      console.error('청구서 업데이트 실패:', error);
    }
  }, [updateBillStatus, refreshData]);

  // 일괄 상태 변경 (useCallback으로 최적화)
  const handleBulkStatusUpdate = useCallback(async (billIds: string[], status: PortvilleBill['status']) => {
    try {
      // 병렬 처리로 성능 개선
      await Promise.all(
        billIds.map(billId => updateBillStatus(billId, status))
      );

      // 데이터 새로고침
      await refreshData();

      // 성공 메시지
      alert(`${billIds.length}개의 청구서 상태가 성공적으로 변경되었습니다.`);
    } catch (error) {
      console.error('일괄 상태 변경 실패:', error);
      alert('일괄 상태 변경에 실패했습니다. 다시 시도해 주세요.');
    }
  }, [updateBillStatus, refreshData]);

  // 가산금 입력 모달 열기
  const handlePenaltyInput = (bill: PortvilleBill) => {
    setPenaltyBill(bill);
    setShowPenaltyModal(true);
  };

  // 가산금 저장
  const handlePenaltySave = async (billId: string, penaltyAmount: number) => {
    try {
      // 가산금 업데이트 API 호출
      const success = await updateBillPenalty(billId, penaltyAmount);
      
      if (success) {
        // 데이터 새로고침
        await refreshData();
        
        // 성공 메시지
        alert('가산금이 성공적으로 저장되었습니다.');
      } else {
        throw new Error('가산금 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('가산금 저장 실패:', error);
      throw error;
    }
  };

  // 청구서 삭제
  const handleDeleteBill = async (billId: string) => {
    if (window.confirm('정말 이 청구서를 삭제하시겠습니까?\n\n주의: 이미 납부된 청구서는 삭제할 수 없습니다.')) {
      try {
        await deleteBill(billId);
        await refreshData();
        alert('청구서가 성공적으로 삭제되었습니다.');
      } catch (error: any) {
        console.error('청구서 삭제 실패:', error);
        
        // 에러 메시지 표시
        if (error.code === 'CANNOT_DELETE_PAID_BILL') {
          alert('이미 납부된 청구서는 삭제할 수 없습니다.');
        } else if (error.code === 'BILL_NOT_FOUND') {
          alert('삭제하려는 청구서가 존재하지 않습니다.');
        } else {
          alert('청구서 삭제에 실패했습니다. 다시 시도해 주세요.');
        }
      }
    }
  };

  // 개별 청구서 PDF 출력 (useCallback으로 최적화)
  const handlePrintBillPDF = useCallback((bill: PortvilleBill) => {
    try {
      generateBillPDF(bill);
    } catch (error) {
      console.error('PDF 생성 실패:', error);
      alert('PDF 생성에 실패했습니다. 다시 시도해 주세요.');
    }
  }, []);

  // 선택된 청구서들 PDF 일괄 출력 (useCallback으로 최적화)
  const handlePrintSelectedBillsPDF = useCallback(() => {
    if (filteredBills.length === 0) {
      alert('출력할 청구서가 없습니다.');
      return;
    }

    try {
      generateMultipleBillsPDF(filteredBills);
    } catch (error) {
      console.error('PDF 생성 실패:', error);
      alert('PDF 생성에 실패했습니다. 다시 시도해 주세요.');
    }
  }, [filteredBills]);

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">청구서 관리</h1>
            <p className="text-gray-600 mt-1">포트빌 월별 관리비 청구서를 관리합니다</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrintSelectedBillsPDF}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              PDF 일괄 출력
            </button>
            <button
              onClick={handleGenerateBills}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              청구서 생성
            </button>
          </div>
        </div>

        {/* 필터 및 검색 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* 연도 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">연도</label>
            <select
              value={selectedPeriod.year}
              onChange={(e) => setSelectedPeriod(prev => ({...prev, year: parseInt(e.target.value)}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
          </div>

          {/* 월 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">월</label>
            <select
              value={selectedPeriod.month}
              onChange={(e) => setSelectedPeriod(prev => ({...prev, month: parseInt(e.target.value)}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>{month}월</option>
              ))}
            </select>
          </div>

          {/* 상태 필터 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">전체</option>
              <option value="pending">대기중</option>
              <option value="paid">납부완료</option>
              <option value="overdue">연체</option>
              <option value="cancelled">취소</option>
            </select>
          </div>

          {/* 검색 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">검색</label>
            <input
              type="text"
              placeholder="호실번호 또는 입주자명"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-blue-600">총 청구서</div>
            <div className="text-2xl font-bold text-blue-900">{statistics.total}</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-yellow-600">대기중</div>
            <div className="text-2xl font-bold text-yellow-900">{statistics.pending}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-green-600">납부완료</div>
            <div className="text-2xl font-bold text-green-900">{statistics.paid}</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-red-600">연체</div>
            <div className="text-2xl font-bold text-red-900">{statistics.overdue}</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-gray-600">총 금액</div>
            <div className="text-2xl font-bold text-gray-900">
              {statistics.totalAmount.toLocaleString()}원
            </div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-indigo-600">수납률</div>
            <div className="text-2xl font-bold text-indigo-900">
              {statistics.paymentRate.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* 에러 표시 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* 청구서 목록 */}
      <div className="bg-white shadow rounded-lg">
        <BillsTable
          bills={filteredBills}
          loading={loading}
          onBillDetail={handleBillDetail}
          onUpdateBill={handleUpdateBill}
          onDeleteBill={handleDeleteBill}
          onPrintPDF={handlePrintBillPDF}
          onBulkStatusUpdate={handleBulkStatusUpdate}
          onPenaltyInput={handlePenaltyInput}
        />
      </div>

      {/* 모달들 */}
      {showDetailModal && selectedBill && (
        <BillDetailModal
          bill={selectedBill}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedBill(null);
          }}
          onUpdate={handleUpdateBill}
        />
      )}

      {showGenerationModal && (
        <BillGenerationModal
          onClose={() => setShowGenerationModal(false)}
          onGenerate={async (request) => {
            try {
              await generateBills(request);
              await refreshData();
              setShowGenerationModal(false);
            } catch (error) {
              console.error('청구서 생성 실패:', error);
            }
          }}
          defaultPeriod={selectedPeriod}
        />
      )}

      {showPenaltyModal && penaltyBill && (
        <PenaltyInputModal
          bill={penaltyBill}
          isOpen={showPenaltyModal}
          onClose={() => {
            setShowPenaltyModal(false);
            setPenaltyBill(null);
          }}
          onSave={handlePenaltySave}
        />
      )}
    </div>
  );
};

export default BillsManagePage;
